import { Component, Injector, OnDestroy, OnInit, TemplateRef, ViewChild, ChangeDetectorRef, ComponentFactoryResolver } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Cell, Graph, Node, Point, Edge } from '@antv/x6';
import { Store, createSelector } from '@ngrx/store';
import { combineLatest, filter, Subscription } from 'rxjs';
import {
  AiFunctionCall,
  AiFunctionCallControllerService,
  CommentWithRelations,
  CustomFunctionDefinition,
  CustomFunctionDefinitionControllerService,
  CustomFunctionDefinitionWithRelations,
  DiagramCommentWithRelations,
  NewDiagramComment,
  PersonWithRelations,
  ProjectModelEndpointWithRelations,
  ProjectModelWithRelations,
  ProjectWithRelations,
  WorkflowControllerService,
  WorkflowEventPartial,
  WorkflowEventWithRelations,
  WorkflowStepFunction,
  WorkflowStepFunctionPublishedEventOnFailure,
  WorkflowStepFunctionPublishedEventOnSuccess,
  WorkflowStepFunctionSubscribedEvent,
  WorkflowStepFunctionWithRelations
} from '@rappider/rappider-sdk';
import { isEveryDataFetched } from '../../utils/workflow-function-event.selector';
import * as WorkflowEventActions from 'libs/project/src/lib/states/workflow-event/workflow-event.actions';
import * as WorkflowStepFunctionActions from 'libs/project/src/lib/states/workflow-step-function/workflow-step-function.actions';
import { DataTransformService, NotificationService } from '@rappider/services';
import { v4 } from 'uuid';
import { FormLayout, HeadingComponentConfig, HeadingType, Menu, MenuActionBehavior, ButtonComponentConfig, ButtonType, EntitySelectionConfig } from '@rappider/rappider-components/utils';
import { EventMode } from 'libs/workflow-event/src/lib/utils/event-mode.enum';
import { WorkflowStepFunctionMode as StepFunctionModeEnum } from 'libs/workflow-event/src/lib/utils/step-function-mode.enum';
import { WorkflowEvent } from '@rappider/api-sdk';
import { WorkflowStepFunctionDataTransformationComponentMode } from 'libs/workflow-step-function/src/lib/models/workflow-step-function-data-transformation-component-mode.enum';
import { DeleteWorkflowStepFunction, GetPostDataTransformationData, GetPreDataTransformationData } from 'libs/project/src/lib/states/workflow-step-function/workflow-step-function.actions';
import { PreDataTransformationData, PostDataTransformationData } from '@rappider/shared/interfaces';
import { WorkflowFunctionTypeOptions } from 'libs/workflow-step-function/src/lib/components/workflow-step-function-form/utils/workflow-functions-options.enum';
import { getWorkflowFunctionsAndTemplates } from 'libs/custom-function/src/lib/components/custom-function-crud-modal-wrapper/utils/get-custom-functions-and-templates-selector';
import { CommentCategory } from 'libs/comment/src/lib/utils/comment-category.enum';
import { CreateDiagramComment, DeleteDiagramComment, UpdateDiagramComment } from 'libs/comment/src/lib/state/diagram-comment/diagram-comment.actions';
import { register } from '@antv/x6-angular-shape';
import { INTEGRATION_FUNCTION_TEMPLATE_SELECTOR_MENU, STEP_FUNCTION_TYPE_SELECTOR_MENU, WorkflowStepFunctionTypeOption } from '../../utils/diagram-configs';
import { WorkflowService } from '../../services/workflow.service';
import { selectAiFunctionCalls } from 'libs/shared/src/lib/states/ai-function-call';
import { IconType } from '@rappider/rappider-components/utils';
import { AI_FUNCTION_CALL_ENTITY_META_CONFIG } from 'libs/ai-integration/src/lib/configs/ai-function-calls.entity.config';
import { EntityService } from 'libs/ai-integration/src/lib/services/entity.service';
import { EntityConfigInterface } from '@rappider/rappider-components';
import { GraphService } from '../../services/graph.service';
import { DiagramNodeToolbarComponent } from '../diagram-node-toolbar/diagram-node-toolbar.component';
import { UpdateCustomFunction } from 'libs/custom-function/src/lib/state/custom-function.actions';
import { DeleteWorkflowByEventId } from 'libs/diagram-editor/src/lib/state/diagram-state/diagram.actions';
import { DeleteWorkflowEvent } from 'libs/project/src/lib/states/workflow-event/workflow-event.actions';
import { workflowEventDeleteAllWorkflowButtonConfig, workflowEventDeleteFollowingStepsButtonConfig } from '@rappider/shared/configs';
import { workflowEventDeleteAllWorkflowConfig } from '../../utils/diagram-button.config';

export const HORIZONTAL_GAP_BETWEEN_COLUMNS = 200;
export const VERTICAL_GAP_BETWEEN_ROWS_OF_NODES = 50;

export const STEP_FUNCTION_WIDTH = 182;
export const STEP_FUNCTION_HEIGHT = 100;

export const MAX_LABEL_LENGTH = 28;

export const EVENT_WIDTH = 50;
export const EVENT_HEIGHT = 50;

export const STEP_FUNCTION_SHAPE_NAME_PREFIX = 'workflowStepFunction';
export const EVENT_SHAPE_NAME_PREFIX = 'workflowEvent';
export const PLACEHOLDER_SHAPE_SUFFIX = 'Placeholder';

export enum SettingViewMode {
  FunctionTypeSelector = 'FunctionTypeSelector',
  IntegrationFunctions = 'IntegrationFunctions',
  AIGeneratedCustomFunction = 'AIGeneratedCustomFunction',
  DataTransformation = 'DataTransformation',
  ProjectEndpoints = 'ProjectEndpoints',
  WorkflowEventSettings = 'WorkflowEventSettings',
  WorkflowStepFunctionSettings = 'WorkflowStepFunctionSettings'
}


@Component({
  selector: 'rappider-diagram-wrapper',
  templateUrl: './diagram-wrapper.component.html',
  styleUrls: ['./diagram-wrapper.component.scss'],
})
export class DiagramWrapperComponent implements OnInit, OnDestroy {
  @ViewChild('commentsTemplate', { static: true }) commentsTemplate: TemplateRef<void>;

  // #region Properties
  subscriptions!: Subscription[];
  workflowEvents!: WorkflowEventWithRelations[];
  workflowStepFunctions!: WorkflowStepFunctionWithRelations[];
  private workflowEventMap = new Map<string, WorkflowEventWithRelations>();
  private workflowStepFunctionMap = new Map<string, WorkflowStepFunctionWithRelations>();
  graph!: Graph;
  isEveryDataFetched!: boolean;
  subscribedEvents!: WorkflowStepFunctionSubscribedEvent[];
  publishedEventsOnSuccess!: WorkflowStepFunctionPublishedEventOnSuccess[];
  publishedEventsOnFailure!: WorkflowStepFunctionPublishedEventOnFailure[];
  isDiagramBuilt = false;
  starterEventIds!: string[];
  menuVisibility = true;
  placeholderEventData?: { id: string; stepFunctionId: string; tempId: string; tempEdgeId: string; type: 'success' | 'failure' | 'subscribe' };
  placeholderStepFunctionData?: { id: string; tempId: string; tempEdgeId: string; subscribedEventIds: string[] };
  isAddEventMenuVisible = true;
  isAddStepFunctionMenuVisible = true;
  lastEnteredNodeId!: string;
  selectedNode: Node | undefined;
  initialStepFunctionData!: Record<string, unknown>;
  formLayout = FormLayout.Vertical;
  eventMode: EventMode;
  stepFunctionMode: StepFunctionModeEnum;
  selectedWorkflowEvent: WorkflowEvent;
  selectedWorkflowStepFunction: WorkflowStepFunction | WorkflowStepFunctionWithRelations;
  WorkflowStepFunctionMode = StepFunctionModeEnum;
  eventFormSubmitButtonLoading: boolean;
  eventFormCancelButtonLoading: boolean;
  stepFunctionFormSubmitButtonLoading: boolean;
  zoomButtonVisibility = false;
  customFunctions: CustomFunctionDefinition[];
  workflowFunctions: CustomFunctionDefinition[];
  templateWorkflowFunctions: CustomFunctionDefinition[];
  selectedFunctionMode: string;
  islastActiveItemTypeEvent = true;
  activeProject: ProjectWithRelations;
  activePerson: PersonWithRelations;
  displayedComments: CommentWithRelations[];
  comments: CommentWithRelations[];
  isCommentsLoading: boolean;
  isCommentsNodeVisible: boolean;
  aiFunctionCalls: AiFunctionCall[] | undefined;
  projectModelEndpoints: ProjectModelEndpointWithRelations[] | undefined;
  projectModels: ProjectModelWithRelations[] | undefined;
  templateCustomFunctionDefinitions: CustomFunctionDefinition[] | undefined;
  dataTransformationModalVisibility = false;
  isDataTransformationLoading = false;
  dataTransformationId: string;
  sourceSchema: any;
  targetSchema: any;
  DataTransformationComponentMode = WorkflowStepFunctionDataTransformationComponentMode;
  activeCommentCell: Cell;
  activeCommentNode: Node;
  showIntegrationFunctions = false;
  showAIGeneratedCustomFunctionForm = false;
  workflowStepFunctionFormHeading: HeadingComponentConfig = { content: 'Workflow Step Function', type: HeadingType.H4 };
  createCustomFunctionWithAIFormConfig = { items: [{ title: 'Describe What You Want to Do', type: 'textarea', fieldName: 'description', showRequiredSign: true, default: null, options: [], inputComponentSize: { xs: 24, sm: 24, md: 12, lg: 8, xxl: 6 }, }], formLayout: 'vertical', submitButton: { text: 'Generate', visible: true, type: ButtonType.Primary }, cancelButton: { text: 'Cancel', visible: true, type: ButtonType.Default }, };
  selectProjectEndpointFormConfig = { items: [{ title: 'Select Project Endpoint', type: 'select', fieldName: 'projectEndpointId', showRequiredSign: true, default: null, options: [], inputComponentSize: { xs: 24, sm: 24, md: 12, lg: 8, xxl: 6 }, }], formLayout: 'vertical', submitButton: { text: 'Add', visible: true, type: ButtonType.Primary }, cancelButton: { text: 'Cancel', visible: true, type: ButtonType.Default }, };
  projectEndpointOptionsForEndpoints = { fieldName: 'projectEndpointId', options: [{ value: null, key: 'Project Endpoints are loading ... ' }] };
  projectEndpointOptions: EntitySelectionConfig[] = [this.projectEndpointOptionsForEndpoints];
  STEP_FUNCTION_TYPE_SELECTOR_MENU = STEP_FUNCTION_TYPE_SELECTOR_MENU;
  integrationFunctionTemplateSelectorMenu = INTEGRATION_FUNCTION_TEMPLATE_SELECTOR_MENU;
  showFunctionTypeSelector = true;
  showProjectEndpointsForm = false;
  preDTButtonConfig: ButtonComponentConfig = { text: 'DATA_TRANSFORMATION_MODULE.PRE_DATA_TRANSFORMATION', icon: { name: 'fal fa-database', type: IconType.FontAwesome }, block: true };
  postDTButtonConfig: ButtonComponentConfig = { text: 'DATA_TRANSFORMATION_MODULE.POST_DATA_TRANSFORMATION', icon: { name: 'fad fa-database', type: IconType.FontAwesome }, block: true };
  floatingButtonMenuOptions = [{ key: '%50', label: '%50' }, { key: '%75', label: '%75' }, { key: '%100', label: '%100' }, { key: '%150', label: '%150' }, { key: '%200', label: '%200' }, { key: 'zoomToFit', label: 'Fit to Screen' }, { key: 'zoomIn', label: 'Zoom In (0.2)' }, { key: 'zoomOut', label: 'Zoom Out (-0.2)' }];
  customFunctionFormHiddenFields: string[] = ['serviceName', 'functionName', 'description',];
  zoomLevel = 100;
  aiFunctionCallEntityConfig: EntityConfigInterface;
  viewMode: SettingViewMode = SettingViewMode.FunctionTypeSelector;
  SettingViewMode = SettingViewMode;
  workflowStepFunctionDeleteButtonConfig = workflowEventDeleteAllWorkflowConfig;
  workflowEventDeleteAllWorkflowButtonConfig;

  selectedCustomFunctionValue: CustomFunctionDefinitionWithRelations | undefined;

  // New Properties for Filtering Logic
  startNodeId: string | null = null;
  starterNodes: { id: string, name: string }[] = [];
  // #endregion

  constructor(
    private store: Store<any>,
    private dataTransformService: DataTransformService,
    private notificationService: NotificationService,
    private injector: Injector,
    private graphService: GraphService,
    private entityService: EntityService,
    private workflowService: WorkflowService,
    private customFunctionDefinitionControllerService: CustomFunctionDefinitionControllerService,
    private aiFunctionCallControllerService: AiFunctionCallControllerService,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  // #region Lifecycle and Subscriptions

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.startNodeId = params.get('id');
      if (this.isDiagramBuilt) {
        this.rebuildDiagram();
      }
    });

    this.initializeDiagram();
    this.listenEvents();
    this.subscribeToData();
    this.refreshSyncedRemoteData();
    this.setAiFunctionCallEntityConfig();
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(sub => sub.unsubscribe());
  }

  refreshSyncedRemoteData() {
    this.workflowService.refreshSyncedRemoteData(this.activeProject?.id);
  }

  setAiFunctionCallEntityConfig() {
    this.aiFunctionCallEntityConfig = this.entityService.generateEntityConfig(AI_FUNCTION_CALL_ENTITY_META_CONFIG);
    this.aiFunctionCallEntityConfig.config.updateItemFormConfig.submitButton = { visible: false };
    const hiddenFields = ['name', 'functionPrompt', 'index', 'postWorkflowStepFunctionId', 'exposeAsFeature'];
    hiddenFields.forEach(field => {
      const index = this.aiFunctionCallEntityConfig?.config?.updateItemFormConfig?.items?.findIndex(item => item.fieldName === field);
      if (index > -1) {
        this.aiFunctionCallEntityConfig.config.updateItemFormConfig.items.splice(index, 1);
      }
    });
  }

  // You can remove these methods:
  // - subscribeToWorkflowEvents()
  // - subscribeToWorkflowStepFunctions()
  // - subscribeToWorkflowStepFunctionSubscribedEvents()
  // - subscribeToWorkflowStepFunctionPublishedEventsOnSuccess()
  // - subscribeToWorkflowStepFunctionPublishedEventsOnFailure()
  // And call this new method from subscribeToData()

  subscribeToData() {
    this.subscriptions = [
      this.subscribeToActivePerson(),
      this.subscribeToActiveProject(),
      this.subscribeToWorkflowEvents(),
      this.subscribeToWorkflowFunctionsAndTemplates(),
      this.subscribeToWorkflowStepFunctions(),
      this.subscribeToWorkflowStepFunctionSubscribedEvents(),
      this.subscribeToWorkflowStepFunctionPublishedEventsOnSuccess(),
      this.subscribeToWorkflowStepFunctionPublishedEventsOnFailure(),
      this.subscribeToLastCreatedPlaceholderId(),
      this.subscribeToWorkflowFetchStatus(),
      this.subscribeToEventFormSubmitLoading(),
      this.subscribeToEventFormCancelLoading(),
      this.subscribeToStepFunctionFormSubmitLoading(),
      this.subscribeToDataTransformationLoading(),
      this.subscribeToPreDataTransformation(),
      this.subscribeToPostDataTransformation(),
      this.subscribeToCustomFunction(),
      this.subscribeToWFDiagramComments(),
      this.subscribeToCommentsLoading(),
      this.subscribeToAiFunctionCalls(),
      this.subscribeToProjectModels(),
      this.subscribeToProjectModelEndpoints(),
      this.subscribeToTemplateCustomFunctionDefinitions(),
      this.subscribeToServiceActivatedRouteQueryParams()
    ];
  }

  subscribeToDiagramData() {
    const sources = {
      events: this.store.select(state => state.workflowEvent.data),
      steps: this.store.select(state => state.workflowStepFunction.data),
      subscribed: this.store.select(state => state.workflowStepFunctionSubscribedEvent.data),
      publishedSuccess: this.store.select(state => state.workflowStepFunctionPublishedEventOnSuccess.data),
      publishedFailure: this.store.select(state => state.workflowStepFunctionPublishedEventOnFailure.data),
      isFetched: this.store.select(isEveryDataFetched)
    };

    return combineLatest(sources).pipe(
      // Ensure we don't try to build until all data has been fetched
      filter(data => data.isFetched)
    ).subscribe(data => {
      // Assign all data at once
      this.workflowEvents = data.events || [];
      this.workflowStepFunctions = data.steps || [];
      this.subscribedEvents = data.subscribed || [];
      this.publishedEventsOnSuccess = data.publishedSuccess || [];
      this.publishedEventsOnFailure = data.publishedFailure || [];

      // Update internal maps and starter nodes
      this.updateWorkflowEventMap();
      this.updateWorkflowStepFunctionMap();
      this.updateStarterNodes();

      // Now, rebuild the entire diagram with complete and synchronized data
      this.rebuildDiagram();
    });
  }

  private updateWorkflowEventMap() {
    this.workflowEventMap.clear();
    this.workflowEvents?.forEach(event => this.workflowEventMap.set(event.id, event));
  }

  private updateWorkflowStepFunctionMap() {
    this.workflowStepFunctionMap.clear();
    this.workflowStepFunctions?.forEach(step => this.workflowStepFunctionMap.set(step.id, step));
  }

  subscribeToCustomFunction() {
    return this.store.select(state => state.customFunction?.data).subscribe((customFunctions: CustomFunctionDefinition[]) => {
      if (customFunctions) {
        this.customFunctions = customFunctions;
      }
    });
  }

  subscribeToWorkflowFetchStatus() {
    return this.store.select(<any>isEveryDataFetched).subscribe((isEveryDataFetched) => {
      this.isEveryDataFetched = isEveryDataFetched;
      if (this.isEveryDataFetched && !this.isDiagramBuilt) {
        this.rebuildDiagram();
      }
    });
  }

  subscribeToWorkflowEvents() {
    return this.store.select(createSelector(
      (state: any) => state.workflowEvent.data,
      (state: any) => state.diagramComment.data,
      (workflowEvents: WorkflowEventWithRelations[], nodeComments: any[]) => ({
        workflowEvents: (workflowEvents || []).map(event => ({
          ...event,
          comments: nodeComments?.filter(comment => comment?.relatedEntityId === event.id) || [],
          index: event.index ?? 999999
        })),
      })
    )).subscribe(data => {
      const changes = this.dataTransformService.compareArrays(this.workflowEvents, data.workflowEvents, 'id', true);
      this.workflowEvents = data.workflowEvents;
      this.updateWorkflowEventMap();
      this.updateStarterNodes();

      if (this.startNodeId) {
        this.rebuildDiagram();
      } else if (this.isDiagramBuilt && (changes.createdArray.length || changes.deletedArray.length || changes.updatedArray.length)) {
        changes.createdArray.forEach(event => this.graph.addNode(this.createEventNode(event)));
        changes.deletedArray.forEach((event: WorkflowEventWithRelations) => this.graph.removeNode(event.id));
        if (changes.deletedArray?.length) {
          this.resetSelectedNode();
        }
        changes.updatedArray.forEach(item => {
          const node = this.graph.getCellById(item.id);
          if (node) {
            node.prop('label', this.getTrimmedLabel(item.name));
          }
        });
        if (changes.createdArray.length || changes.deletedArray.length) {
          this.layout();
        }
      }
    });
  }

  subscribeToWorkflowFunctionsAndTemplates() {
    return this.store.select(<any>getWorkflowFunctionsAndTemplates).subscribe(data => {
      if (data) {
        this.workflowFunctions = data.workflowFunctions;
        this.templateWorkflowFunctions = data.templateWorkflowFunctions;
      }
    });
  }

  subscribeToAiFunctionCalls() {
    return this.store.select(selectAiFunctionCalls).subscribe((aiFunctionCalls: AiFunctionCall[]) => {
      if (aiFunctionCalls) {
        this.aiFunctionCalls = aiFunctionCalls;
      }
    });
  }

  subscribeToTemplateCustomFunctionDefinitions() {
    return this.store.select(state => state.customFunction?.templateWorkflowFunctions)
      .subscribe((templateCustomFunctionDefinitions: CustomFunctionDefinitionWithRelations[]) => {
        this.templateCustomFunctionDefinitions = templateCustomFunctionDefinitions || [];
        if (this.templateCustomFunctionDefinitions?.length) {
          const itemsMenu: Menu[] = this.templateCustomFunctionDefinitions.map(templateCustomFunctionDefinition => ({
            label: templateCustomFunctionDefinition.title || `${templateCustomFunctionDefinition.serviceName}.${templateCustomFunctionDefinition.functionName}`,
            key: templateCustomFunctionDefinition.id,
            description: templateCustomFunctionDefinition.description,
            icon: {
              name: templateCustomFunctionDefinition?.menu || templateCustomFunctionDefinition?.metadata?.find(item => item.key === 'icon')?.value || 'fa-regular fa-code',
            },
            actionBehavior: MenuActionBehavior.Emit,
            data: templateCustomFunctionDefinition
          }));

          this.integrationFunctionTemplateSelectorMenu.items = itemsMenu;
        }
      });
  }

  subscribeToProjectModelEndpoints() {
    return this.store.select(state => state.projectModelEndpoint?.data)
      .subscribe((projectModelEndpoints: ProjectModelEndpointWithRelations[]) => {
        this.projectModelEndpoints = projectModelEndpoints;
        if (this.projectModels?.length) {
          this.generateProjectModelEndpointOptions();
        }
      });
  }

  subscribeToProjectModels() {
    return this.store.select(state => state.projectModel?.data)
      .subscribe(projectModels => {
        this.projectModels = projectModels;
        if (this.projectModelEndpoints?.length) {
          this.generateProjectModelEndpointOptions();
        }
      });
  }

  generateProjectModelEndpointOptions() {
    if (this.projectModelEndpoints?.length && this.projectModels?.length) {
      this.projectEndpointOptionsForEndpoints.options = this.projectModelEndpoints?.map(endpoint => {
        const projectModel = this.projectModels?.find(model => model?.id === endpoint?.modelId);
        let operation: string | undefined;
        switch (endpoint?.name) {
          case 'find': operation = '[GET]'; break;
          case 'count': operation = '[COUNT]'; break;
          case 'findById': operation = '[GET] by Id'; break;
          case 'create': operation = '[CREATE]'; break;
          case 'createAll': operation = '[CREATE] Multiple'; break;
          case 'updateById': operation = '[UPDATE] by Id'; break;
          case 'replaceById': operation = '[REPLACE] by Id'; break;
          case 'deleteById': operation = '[DELETE] by Id'; break;
        }
        return {
          value: endpoint?.id,
          key: `${projectModel?.name} ${operation?.toUpperCase() || endpoint?.name.toUpperCase()} (${endpoint?.description})`,
          description: endpoint?.description,
        };
      }) || [];
      this.projectEndpointOptions = [this.projectEndpointOptionsForEndpoints];
    }
  }

  subscribeToWorkflowStepFunctions() {
    return this.store.select(createSelector(
      (state: any) => state.workflowStepFunction.data,
      (state: any) => state.diagramComment.data,
      (workflowStepFunctions: WorkflowStepFunctionWithRelations[], nodeComments: any[]) => ({
        workflowStepFunctions: (workflowStepFunctions || []).map(step => ({
          ...step,
          comments: nodeComments?.filter(comment => comment?.relatedEntityId === step.id) || []
        })),
      })
    )).subscribe(data => {
      const changes = this.dataTransformService.compareArrays(this.workflowStepFunctions, data.workflowStepFunctions, 'id', true);
      this.workflowStepFunctions = data.workflowStepFunctions;
      this.updateWorkflowStepFunctionMap();
      this.updateStarterNodes();

      if (this.startNodeId) {
        this.rebuildDiagram();
      } else if (this.isDiagramBuilt && (changes.createdArray.length || changes.deletedArray.length || changes.updatedArray.length)) {
        changes.createdArray.forEach(step => this.graph.addNode(this.createStepFunctionNode(step)));
        changes.deletedArray.forEach((step: WorkflowStepFunctionWithRelations) => this.graph.removeNode(step.id));
        if (changes.deletedArray?.length) {
          this.resetSelectedNode();
        }
        changes.updatedArray.forEach(item => {
          const node = this.graph.getCellById(item.id);
          if (node) {
            node.prop('label', this.getTrimmedLabel(item.name));
          }
        });
        if (changes.createdArray.length || changes.deletedArray.length) {
          this.layout();
        }
      }
    });
  }

  subscribeToWorkflowStepFunctionSubscribedEvents() {
    return this.store.select(state => state.workflowStepFunctionSubscribedEvent.data).subscribe(newData => {
      const newSubscribedEvents = newData || [];
      const changes = this.dataTransformService.compareArrays(this.subscribedEvents, newSubscribedEvents, 'id');
      this.subscribedEvents = newSubscribedEvents;
      this.updateStarterNodes();

      if (this.startNodeId) {
        this.rebuildDiagram();
      } else if (this.isDiagramBuilt && (changes.createdArray.length || changes.deletedArray.length)) {
        changes.createdArray.forEach(edge => this.graph.addEdge(this.createSubscribedEdge(edge)));
        changes.deletedArray.forEach((edge: WorkflowStepFunctionSubscribedEvent) => this.graph.removeEdge(edge.id));
        this.layout();
      }
    });
  }

  subscribeToWorkflowStepFunctionPublishedEventsOnSuccess() {
    return this.store.select(state => state.workflowStepFunctionPublishedEventOnSuccess.data).subscribe(newData => {
      const newPublishedOnSuccess = newData || [];
      const changes = this.dataTransformService.compareArrays(this.publishedEventsOnSuccess, newPublishedOnSuccess, 'id');
      this.publishedEventsOnSuccess = newPublishedOnSuccess;
      this.updateStarterNodes();

      if (this.startNodeId) {
        this.rebuildDiagram();
      } else if (this.isDiagramBuilt && (changes.createdArray.length || changes.deletedArray.length)) {
        changes.createdArray.forEach(edge => this.graph.addEdge(this.createPublishedEdge(edge, 'success')));
        changes.deletedArray.forEach((edge: WorkflowStepFunctionPublishedEventOnSuccess) => this.graph.removeEdge(edge.id));
        this.layout();
      }
    });
  }

  subscribeToWorkflowStepFunctionPublishedEventsOnFailure() {
    return this.store.select(state => state.workflowStepFunctionPublishedEventOnFailure.data).subscribe(newData => {
      const newPublishedOnFailure = newData || [];
      const changes = this.dataTransformService.compareArrays(this.publishedEventsOnFailure, newPublishedOnFailure, 'id');
      this.publishedEventsOnFailure = newPublishedOnFailure;
      this.updateStarterNodes();

      if (this.startNodeId) {
        this.rebuildDiagram();
      } else if (this.isDiagramBuilt && (changes.createdArray.length || changes.deletedArray.length)) {
        changes.createdArray.forEach(edge => this.graph.addEdge(this.createPublishedEdge(edge, 'failure')));
        changes.deletedArray.forEach((edge: WorkflowStepFunctionPublishedEventOnFailure) => this.graph.removeEdge(edge.id));
        this.layout();
      }
    });
  }

  subscribeToLastCreatedPlaceholderId() {
    return this.store.select(state => state.wfDiagramState.lastCreatedPlaceholderDataId).subscribe(id => {
      if (id === this.placeholderEventData?.id) {
        this.removePlaceholderEvent();
        this.layout();
      } else if (id === this.placeholderStepFunctionData?.id) {
        this.removePlaceholderStepFunction();
        this.layout();
      }
    });
  }

  subscribeToEventFormSubmitLoading() {
    return this.store.select(state => state.workflowEvent?.isLoading).subscribe(loading => this.eventFormSubmitButtonLoading = loading);
  }

  subscribeToEventFormCancelLoading() {
    return this.store.select(state => state.workflowEvent?.cancelButtonLoading).subscribe(loading => this.eventFormCancelButtonLoading = loading);
  }

  subscribeToStepFunctionFormSubmitLoading() {
    return this.store.select(state => state.workflowStepFunction?.isLoading).subscribe(loading => this.stepFunctionFormSubmitButtonLoading = loading);
  }

  subscribeToDataTransformationLoading() {
    return this.store.select(state => state.workflowStepFunction?.isDataTransformationLoading).subscribe(isLoading => {
      this.isDataTransformationLoading = isLoading;
    });
  }

  subscribeToPreDataTransformation() {
    return this.store.select(state => state.workflowStepFunction?.preDataTransformationData).subscribe((preDT: PreDataTransformationData) => {
      this.dataTransformationId = preDT?.preDataTransformationId;
      this.sourceSchema = preDT?.preSourceJsonSchema;
      this.targetSchema = preDT?.preTargetJsonSchema;
    });
  }

  subscribeToPostDataTransformation() {
    return this.store.select(state => state.workflowStepFunction?.postDataTransformationData).subscribe((postDT: PostDataTransformationData) => {
      this.dataTransformationId = postDT?.postDataTransformationId;
      this.sourceSchema = postDT?.postSourceJsonSchema;
      this.targetSchema = postDT?.postTargetJsonSchema;
    });
  }

  subscribeToActiveProject() {
    return this.store.select(state => state.activeProject.data).subscribe(project => {
      this.activeProject = project;
    });
  }

  subscribeToActivePerson() {
    return this.store.select(state => state.auth?.activePerson).subscribe((activePerson: PersonWithRelations) => {
      this.activePerson = activePerson;
    });
  }

  subscribeToServiceActivatedRouteQueryParams() {
    return this.route.queryParams.subscribe(q => {
      if (q?.filteredWorkflowId) {
        this.graph.clearCells();
        this.startNodeId = q.filteredWorkflowId;
        this.rebuildDiagram();
      } else {
        this.graph.clearCells();
        this.startNodeId = undefined;
        this.rebuildDiagram();
      }
    });
  }

  subscribeToWFDiagramComments() {
    return this.store.select(createSelector(
      (state: any) => state.comment?.data,
      (state: any) => state.diagramComment?.data,
      (comments: CommentWithRelations[], diagramComments: DiagramCommentWithRelations[]) => {
        const wfDiagramComments = comments?.filter(comment =>
          comment.category === CommentCategory.BEWorkflowEvent ||
          comment.category === CommentCategory.BEWorkflowStepFunction);
        const wfDiagramCommentsWithRelations = wfDiagramComments?.map(comment => ({
          ...comment,
          details: diagramComments?.find(c => c.commentId === comment.id)
        }));
        return wfDiagramCommentsWithRelations;
      }
    )).subscribe(data => {
      this.comments = data;
      this.filterCommentsByActiveEntityId();
      this.setActiveCommentCellData();
    });
  }

  subscribeToCommentsLoading() {
    return this.store.select(createSelector(
      (state: any) => state.comment?.isLoading,
      (state: any) => state.diagramComment?.isLoading,
      (commentsLoading, diagramCommentsLoading) => commentsLoading || diagramCommentsLoading
    )).subscribe((isLoading: boolean) => {
      this.isCommentsLoading = isLoading;
      this.setActiveCommentCellLoading();
    });
  }

  // #endregion

  // #region New Methods for Filtering

  updateStarterNodes() {
    if (!this.workflowEvents || !this.workflowStepFunctions || !this.publishedEventsOnSuccess || !this.publishedEventsOnFailure || !this.subscribedEvents) {
      this.starterNodes = [];
      return;
    }

    const allPublishedEventIds = new Set([
      ...this.publishedEventsOnSuccess.map(e => e.workflowEventId),
      ...this.publishedEventsOnFailure.map(e => e.workflowEventId)
    ]);

    const starterEvents = this.workflowEvents
      .filter(event => !allPublishedEventIds.has(event.id))
      .map(event => ({ id: event.id, name: `${event.name} (Event)` }));

    const allTargetedStepFunctionIds = new Set(
      this.subscribedEvents.map(e => e.workflowStepFunctionId)
    );

    const starterStepFunctions = this.workflowStepFunctions
      .filter(step => !allTargetedStepFunctionIds.has(step.id))
      .map(step => ({ id: step.id, name: `${step.name} (Step Function)` }));

    this.starterNodes = [...starterEvents, ...starterStepFunctions].sort((a, b) => a.name.localeCompare(b.name));
  }

  onStarterNodeSelected(nodeId: string | null): void {
    const basePath = '/workflow-diagram';
    if (nodeId) {
      this.graph.clearCells();
      this.startNodeId = nodeId;
      this.rebuildDiagram();
    } else {
      this.graph.clearCells();
      this.startNodeId = undefined;
      this.rebuildDiagram();
    }
  }

  private getFilteredDiagramData(startNodeId: string) {
    const visitedNodeIds = new Set<string>();
    const collectedEdgeIds = new Set<string>();
    const queue: string[] = [startNodeId];

    this.updateWorkflowEventMap();
    this.updateWorkflowStepFunctionMap();

    while (queue.length > 0) {
      const currentNodeId = queue.shift()!;
      if (visitedNodeIds.has(currentNodeId) || !currentNodeId) continue;

      visitedNodeIds.add(currentNodeId);

      const isEvent = this.workflowEventMap.has(currentNodeId);
      const isStep = this.workflowStepFunctionMap.has(currentNodeId);

      if (isEvent) {
        const outgoingEdges = this.subscribedEvents.filter(edge => edge.workflowEventId === currentNodeId);
        for (const edge of outgoingEdges) {
          if (edge.workflowStepFunctionId) queue.push(edge.workflowStepFunctionId);
          collectedEdgeIds.add(edge.id);
        }
      } else if (isStep) {
        const successEdges = this.publishedEventsOnSuccess.filter(edge => edge.workflowStepFunctionId === currentNodeId);
        for (const edge of successEdges) {
          if (edge.workflowEventId) queue.push(edge.workflowEventId);
          collectedEdgeIds.add(edge.id);
        }
        const failureEdges = this.publishedEventsOnFailure.filter(edge => edge.workflowStepFunctionId === currentNodeId);
        for (const edge of failureEdges) {
          if (edge.workflowEventId) queue.push(edge.workflowEventId);
          collectedEdgeIds.add(edge.id);
        }
      }
    }

    return {
      events: this.workflowEvents.filter(e => visitedNodeIds.has(e.id)),
      steps: this.workflowStepFunctions.filter(s => visitedNodeIds.has(s.id)),
      subscribed: this.subscribedEvents.filter(e => collectedEdgeIds.has(e.id)),
      publishedSuccess: this.publishedEventsOnSuccess.filter(e => collectedEdgeIds.has(e.id)),
      publishedFailure: this.publishedEventsOnFailure.filter(e => collectedEdgeIds.has(e.id)),
    };
  }

  // #endregion

  // #region Diagram Initialization and Building (Refactored)

  initializeDiagram() {
    const container = document.getElementById('diagram-container');
    if (container) {
      this.graph = new Graph({
        container,
        connecting: { router: 'orth' },
        grid: true,
        panning: true,
        mousewheel: { enabled: true, zoomAtMousePosition: true, minScale: 0.25, maxScale: 3.0 },
        interacting: { nodeMovable: false, edgeMovable: false, edgeLabelMovable: false, magnetConnectable: true }
      });
      if (this.graph) {
        this.graphService.registerCustomTools();
        this.graphService.registerWorkflowEvent();
        this.graphService.registerWorkflowStepFunction();
        this.graphService.registerEdge();
      }
    }
  }

  private rebuildDiagram() {
    if (!this.graph || !this.isEveryDataFetched) return;

    this.graph.clearCells();

    const data = this.startNodeId
      ? this.getFilteredDiagramData(this.startNodeId)
      : {
        events: this.workflowEvents,
        steps: this.workflowStepFunctions,
        subscribed: this.subscribedEvents,
        publishedSuccess: this.publishedEventsOnSuccess,
        publishedFailure: this.publishedEventsOnFailure
      };

    if (!data.events?.length && !data.steps?.length) {
      this.isDiagramBuilt = true;
      return;
    }

    this.findStarterEvents();

    // remove the edges if there are no nodes for workflowStepFunctionId or workflowEventId
    const publishedSuccessEdges = data.publishedSuccess.filter(edge =>
      this.workflowStepFunctionMap.has(edge.workflowStepFunctionId) && this.workflowEventMap.has(edge.workflowEventId)
    );
    const publishedFailureEdges = data.publishedFailure.filter(edge =>
      this.workflowStepFunctionMap.has(edge.workflowStepFunctionId) && this.workflowEventMap.has(edge.workflowEventId)
    );
    const subscribedEdges = data.subscribed.filter(edge =>
      this.workflowEventMap.has(edge.workflowEventId) && this.workflowStepFunctionMap.has(edge.workflowStepFunctionId)
    );

    this.graph.fromJSON({
      nodes: [
        ...data.events.map(event => this.createEventNode(event)),
        ...data.steps.map(step => this.createStepFunctionNode(step))
      ],
      edges: [
        ...subscribedEdges.map(edge => this.createSubscribedEdge(edge)),
        ...publishedSuccessEdges.map(edge => this.createPublishedEdge(edge, 'success')),
        ...publishedFailureEdges.map(edge => this.createPublishedEdge(edge, 'failure'))
      ]
    });

    this.layout();
    this.isDiagramBuilt = true;
  }

  findStarterEvents() {
    const allPublishedEvents = [...(this.publishedEventsOnFailure || []), ...(this.publishedEventsOnSuccess || [])];
    const starterEvents = (this.workflowEvents || []).filter(event =>
      !allPublishedEvents.some(item => item?.workflowEventId === event?.id)
    );
    this.starterEventIds = starterEvents?.map(item => item.id);
  }

  // #endregion

  // #region Custom Tree Layout Algorithm

  layout() {
    this.graph.batchUpdate(() => {
      const allEdges = this.graph.getEdges();
      const components = this.findConnectedComponents();
      this.sortComponents(components);

      let yOffset = 0;
      for (const component of components) {
        const { positions, height } = this.layoutComponent(component);
        for (const [id, pos] of positions.entries()) {
          const node = this.graph.getCellById(id) as Node;
          if (node) {
            if (node?.shape?.includes(STEP_FUNCTION_SHAPE_NAME_PREFIX)) { // step function
              const STEP_FUNCTION_Y_OFFSET = -1 * (STEP_FUNCTION_HEIGHT - EVENT_HEIGHT) / 2;
              node.position(pos.x, pos.y + STEP_FUNCTION_Y_OFFSET + yOffset);
            } else if (node?.shape?.includes(EVENT_SHAPE_NAME_PREFIX)) { // event
              node.position(pos.x, pos.y + yOffset);
            }
          }
        }
        yOffset += height + (VERTICAL_GAP_BETWEEN_ROWS_OF_NODES * 4);
      }

      allEdges.forEach(edge => this.routeEdge(edge));
    });
  }

  private findConnectedComponents(): { nodes: Node[]; edges: Edge[] }[] {
    const components: { nodes: Node[]; edges: Edge[] }[] = [];
    const visited = new Set<string>();

    this.graph.getNodes().forEach(startNode => {
      if (visited.has(startNode.id)) return;

      const componentNodes: Node[] = [];
      const queue: Node[] = [startNode];
      visited.add(startNode.id);

      while (queue.length > 0) {
        const node = queue.shift()!;
        componentNodes.push(node);
        this.graph.getConnectedEdges(node).forEach(edge => {
          const neighbor = edge.getSourceCell() === node ? edge.getTargetCell() : edge.getSourceCell();
          if (neighbor && neighbor.isNode() && !visited.has(neighbor.id)) {
            visited.add(neighbor.id);
            queue.push(neighbor as Node);
          }
        });
      }

      const nodeIds = new Set(componentNodes.map(n => n.id));
      const componentEdges = this.graph.getEdges().filter(e => nodeIds.has(e.getSourceCellId()) && nodeIds.has(e.getTargetCellId()));
      components.push({ nodes: componentNodes, edges: componentEdges });
    });
    return components;
  }

  private sortComponents(components: { nodes: Node[]; edges: Edge[] }[]): void {
    components.sort((a, b) => {
      const starterA = a.nodes.find(n => this.starterEventIds?.includes(n.id));
      const starterB = b.nodes.find(n => this.starterEventIds?.includes(n.id));

      if (starterA && starterB) {
        const eventA = this.workflowEventMap.get(starterA.id);
        const eventB = this.workflowEventMap.get(starterB.id);
        if (eventA && eventB) {
          const indexA = eventA.index ?? 999999;
          const indexB = eventB.index ?? 999999;
          if (indexA !== indexB) return indexA - indexB;
          return eventA.name.localeCompare(eventB.name);
        }
      }
      if (starterA) return -1;
      if (starterB) return 1;
      return b.nodes.length - a.nodes.length;
    });
  }

  private layoutComponent(component: { nodes: Node[]; edges: Edge[] }): { positions: Map<string, { x: number; y: number }>, height: number } {
    const { nodes, edges } = component;
    const startNode = nodes.find(n => !edges.some(e => e.getTargetCellId() === n.id));
    if (!startNode) return { positions: new Map(), height: 0 }; // Should not happen in a valid tree

    const branchHeights = this.calculateBranchHeights(startNode, edges);
    const positions = this.positionNodes(startNode, edges, branchHeights);

    // Normalize positions and calculate total height
    let minY = Infinity;
    let maxY = -Infinity;
    for (const [id, pos] of positions.entries()) {
      const nodeHeight = this.getNodeHeight(id);
      minY = Math.min(minY, pos.y - nodeHeight / 2);
      maxY = Math.max(maxY, pos.y + nodeHeight / 2);
    }
    const height = maxY - minY;
    const yShift = -minY;

    for (const [id, pos] of positions.entries()) {
      pos.y += yShift;
    }

    return { positions, height };
  }

  private calculateBranchHeights(startNode: Node, allEdges: Edge[], cache = new Map<string, number>()): Map<string, number> {
    const calculate = (nodeId: string): number => {
      if (cache.has(nodeId)) return cache.get(nodeId)!;

      const children = allEdges.filter(edge => edge.getSourceCellId() === nodeId).map(edge => edge.getTargetCellId());
      const nodeHeight = this.getNodeHeight(nodeId);

      if (children.length === 0) {
        cache.set(nodeId, nodeHeight);
        return nodeHeight;
      }

      const childrenTotalHeight = children.reduce((sum, childId) => sum + calculate(childId!), 0);
      const totalHeightWithGaps = childrenTotalHeight + (children.length - 1) * VERTICAL_GAP_BETWEEN_ROWS_OF_NODES;
      const finalHeight = Math.max(nodeHeight, totalHeightWithGaps);
      cache.set(nodeId, finalHeight);
      return finalHeight;
    };
    calculate(startNode.id);
    return cache;
  }

  private positionNodes(startNode: Node, allEdges: Edge[], branchHeights: Map<string, number>): Map<string, { x: number; y: number }> {
    const positions = new Map<string, { x: number; y: number }>();
    const position = (nodeId: string, x: number, y: number) => {
      positions.set(nodeId, { x, y });

      const children = allEdges.filter(edge => edge.getSourceCellId() === nodeId).map(edge => edge.getTargetCellId());
      if (children.length === 0) return;

      if (children.length === 1) {
        const childId = children[0]!;
        const childX = x + this.getNodeWidth(nodeId) / 2 + HORIZONTAL_GAP_BETWEEN_COLUMNS + this.getNodeWidth(childId) / 2;
        position(childId, childX, y);
      } else {
        const totalChildrenHeight = children.reduce((sum, childId) => sum + (branchHeights.get(childId!) || 0), 0) + (children.length - 1) * VERTICAL_GAP_BETWEEN_ROWS_OF_NODES;
        let yCursor = y - totalChildrenHeight / 2;

        children.forEach(childId => {
          const childBranchHeight = branchHeights.get(childId!) || 0;
          const childY = yCursor + childBranchHeight / 2;
          const childX = x + this.getNodeWidth(nodeId) / 2 + HORIZONTAL_GAP_BETWEEN_COLUMNS + this.getNodeWidth(childId!) / 2;
          position(childId!, childX, childY);
          yCursor += childBranchHeight + VERTICAL_GAP_BETWEEN_ROWS_OF_NODES;
        });
      }
    };
    position(startNode.id, 0, 0);
    return positions;
  }

  private routeEdge(edge: Edge) {
    const sourceNode = edge.getSourceNode();
    const targetNode = edge.getTargetNode();
    if (sourceNode && targetNode) {
      const sourceBBox = sourceNode.getBBox();
      const midX = sourceBBox.x + sourceBBox.width + HORIZONTAL_GAP_BETWEEN_COLUMNS / 2;
      edge.setVertices([
        { x: midX, y: sourceBBox.center.y },
        { x: midX, y: targetNode.getBBox().center.y },
      ]);
    }
  }

  private getNodeHeight(nodeId: string): number {
    const node = this.graph.getCellById(nodeId);
    if (!node) return 0;
    return this.isAnEventShape(node.shape) ? EVENT_HEIGHT : STEP_FUNCTION_HEIGHT;
  }

  private getNodeWidth(nodeId: string): number {
    const node = this.graph.getCellById(nodeId);
    if (!node) return 0;
    return this.isAnEventShape(node.shape) ? EVENT_WIDTH : STEP_FUNCTION_WIDTH;
  }

  // #endregion

  // #region Event Handlers and other methods

  getTrimmedLabel(label: string) {
    return label?.length > MAX_LABEL_LENGTH ? label?.substring(0, MAX_LABEL_LENGTH) + '...' : label ?? 'Node';
  }

  listenEvents() {
    this.onMouseEnterNode();
    this.onMouseLeaveNode();
    this.onNodeClick();
    this.onBlankClick();
    this.onGraphScale();
  }

  onGraphScale() {
    this.graph.on('scale', ({ sx }) => {
      this.zoomLevel = Math.round(sx * 100);
    });
  }

  onBlankClick() {
    this.graph.on('blank:click', () => {
      this.resetSelectedNode();
    });
  }

  resetSelectedNode() {
    this.selectedNode = undefined;
    this.selectedWorkflowStepFunction = undefined;
    this.selectedWorkflowEvent = undefined;
    this.viewMode = SettingViewMode.FunctionTypeSelector;
    this.stepFunctionMode = StepFunctionModeEnum.Create;
    this.eventMode = EventMode.Create;
    this.removePlaceholders();
    this.activeCommentNode = undefined;
    this.resetGraphCommentComponentsState();
  }

  isAStepFunctionShape(shape: string) {
    return shape?.toLowerCase()?.includes(STEP_FUNCTION_SHAPE_NAME_PREFIX.toLowerCase());
  }

  isAnEventShape(shape: string) {
    return shape?.toLowerCase()?.includes(EVENT_SHAPE_NAME_PREFIX.toLowerCase());
  }

  isAPlaceholderShape(shape: string) {
    return shape?.toLowerCase()?.includes(PLACEHOLDER_SHAPE_SUFFIX.toLowerCase());
  }

  onNodeClick() {
    this.graph.on('node:click', (args: { node: Node }) => {
      const { node } = args;
      if (this.isAPlaceholderShape(node.shape)) {
        return;
      } else {
        this.setSelectedNode(node);

        if (this.isAStepFunctionShape(node.shape)) {
          this.handleStepFunctionClick(node);
        } else if (this.isAnEventShape(node.shape)) {
          this.handleEventClick(node);
        }
      }
    });
  }

  displaySettingsForNode(node: Node) {
    if (this.isAStepFunctionShape(node.shape)) {
      this.viewMode = SettingViewMode.WorkflowStepFunctionSettings;
    } else if (this.isAnEventShape(node.shape)) {
      this.viewMode = SettingViewMode.WorkflowEventSettings;
    }
  }

  private handleStepFunctionClick(node: Node) {
    const selectedWorkflowStepFunction = this.workflowStepFunctionMap.get(node.id);

    if (!selectedWorkflowStepFunction) {
      return null;
    }
    this.selectedWorkflowStepFunction = {
      ...selectedWorkflowStepFunction,
      subscribedEventIds: selectedWorkflowStepFunction.subscribedEvents?.map(item => item.id),
      publishedEventsOnSuccess: selectedWorkflowStepFunction.publishedEventsOnSuccess?.map(item => item.id),
      publishedEventsOnFailure: selectedWorkflowStepFunction.publishedEventsOnFailure?.map(item => item.id),
      mode: selectedWorkflowStepFunction?.mode ?? 'custom-function-definition'
    };

    if (this.selectedWorkflowStepFunction?.mode === 'custom-function-definition') {

      const customFunctionDefinitionId = selectedWorkflowStepFunction?.customFunctionDefinitionId || selectedWorkflowStepFunction?.customFunction?.id;

      if (customFunctionDefinitionId) {
        this.customFunctionDefinitionControllerService.findById({ id: customFunctionDefinitionId }).subscribe(customFunctionDefinition => {
          this.selectedWorkflowStepFunction.customFunction = customFunctionDefinition;
          this.selectedWorkflowStepFunction.customFunctionDefinitionId = customFunctionDefinition.id;
          this.cdr.detectChanges();
        });
      }
    } else if (this.selectedWorkflowStepFunction?.mode === 'ai-function') {
      const aiFunctionId = selectedWorkflowStepFunction?.aiFunctionId || selectedWorkflowStepFunction?.aiFunction?.id;
      if (aiFunctionId) {
        this.aiFunctionCallControllerService.findById({ id: aiFunctionId }).subscribe(aiFunction => {
          this.selectedWorkflowStepFunction.aiFunction = aiFunction;
          this.selectedWorkflowStepFunction.aiFunctionId = aiFunction.id;
          this.cdr.detectChanges();
        });
      }
    }

    this.stepFunctionMode = StepFunctionModeEnum.Edit;
    this.islastActiveItemTypeEvent = false;
    this.removePlaceholders();
    this.addCommentButtonToActiveNode(node);
    this.activeCommentNode = node;
  }

  onCustomFunctionUpdateFormSubmit(customFunction: CustomFunctionDefinitionWithRelations) {
    const customFunctionId = customFunction.id || this.selectedWorkflowStepFunction?.customFunction?.id;
    this.store.dispatch(UpdateCustomFunction({ id: customFunctionId, customFunction: customFunction, navigateAfterSubmit: false }));
  }

  updateCustomFunctionUsingTheFormValue() {
    // custom function form value is being tracked separately with selectedCustomFunctionValue
    if (this.selectedCustomFunctionValue) {
      const customFunctionId = this.selectedCustomFunctionValue?.id || this.selectedWorkflowStepFunction?.customFunction?.id;
      this.store.dispatch(UpdateCustomFunction({ id: customFunctionId, customFunction: this.selectedCustomFunctionValue, navigateAfterSubmit: false }));
    }
  }

  onCustomFunctionUpdateFormChangedValue(customFunction: CustomFunctionDefinitionWithRelations) {
    console.log('Custom Function Form Value Changed:', customFunction);
    const customFunctionId = customFunction.id || this.selectedWorkflowStepFunction?.customFunction?.id;
    this.selectedCustomFunctionValue = { ...customFunction, id: customFunctionId };
  }

  onAIFunctionUpdateFormSubmit(aiFunction: any) {
    // const aiFunctionId = aiFunction.id || this.selectedWorkflowStepFunction?.aiFunction?.id;
    // this.store.dispatch(UpdateAIFunction({ id: aiFunctionId, aiFunction: aiFunction, navigateAfterSubmit: false }));
  }

  onAIFunctionUpdateFormValueChange(aiFunction: any) {
    // Placeholder
  }

  private handleEventClick(node: Node) {
    this.selectedWorkflowEvent = this.workflowEventMap.get(node.id);
    if (!this.selectedWorkflowEvent) { return; }

    this.islastActiveItemTypeEvent = true;
    this.eventMode = EventMode.Edit;
    this.removePlaceholders();
    this.addCommentButtonToActiveNode(node);
    this.activeCommentNode = node;
    if (this.starterEventIds?.includes(node.id)) {
      this.workflowEventDeleteAllWorkflowButtonConfig = workflowEventDeleteAllWorkflowButtonConfig;
    } else {
      this.workflowEventDeleteAllWorkflowButtonConfig = workflowEventDeleteFollowingStepsButtonConfig;
    }
  }

  private removePlaceholders() {
    this.removePlaceholderEvent();
    this.removePlaceholderStepFunction();
  }

  addFailureEventToStepFunction(cell: Cell) {
    const selectedStepFunctionId = cell?.id;
    const selectedStepFunction = this.workflowStepFunctions?.find(workflowStepFunction => workflowStepFunction.id === selectedStepFunctionId);
    if (selectedStepFunction) {
      this.addPlaceholderEvent('failure', selectedStepFunctionId);
      this.eventMode = EventMode.Create;
      const workflowEvent: any = {
        name: selectedStepFunction?.name ? this.generateNewEventName(selectedStepFunction?.name, 'failure')
          : this.generateNewEventName('New', 'failure'),
        type: this.placeholderEventData.type,
        stepFunctionId: selectedStepFunction?.id,
        inputDataSample: null
      };
      this.onCreateWorkflowEvent(workflowEvent);
    }
  }

  addSuccessEventToStepFunction(cell: Cell) {
    const selectedStepFunctionId = cell?.id;
    const selectedStepFunction = this.workflowStepFunctions?.find(workflowStepFunction => workflowStepFunction.id === selectedStepFunctionId);
    if (selectedStepFunction) {
      this.addPlaceholderEvent('success', selectedStepFunctionId);
      this.eventMode = EventMode.Create;
      const workflowEvent: any = {
        name: selectedStepFunction?.name ? this.generateNewEventName(selectedStepFunction?.name, 'success')
          : this.generateNewEventName('New', 'success'),
        type: this.placeholderEventData.type,
        stepFunctionId: selectedStepFunction?.id,
        inputDataSample: null
      };
      this.onCreateWorkflowEvent(workflowEvent);
    }
  }

  generateNewEventName(prefix: string, type: 'subscribe' | 'success' | 'failure' = 'subscribe'): string {
    const existingNames = this.workflowEvents.map(event => event.name);
    let newName = `${prefix}${type.charAt(0).toUpperCase()}${type.slice(1)}Event`;
    if (existingNames.includes(newName)) {
      let counter = 1;
      while (existingNames.includes(`${newName} ${counter}`)) {
        counter++;
      }
      newName = `${newName} ${counter}`;
    }
    return newName;
  }

  onMouseEnterNode() {
    this.graph.on('node:mouseenter', ({ node }) => {
      if (this.isAStepFunctionShape(node?.shape) && !this.isAPlaceholderShape(node?.shape)) {
        node.addTools([
          {
            name: 'button',
            args: {
              markup: [{ tagName: 'g', children: [{ tagName: 'image', selector: 'add-success', attrs: { ...this.graphService.successPlusIcon } }] }],
              x: '100%', y: 0, offset: { x: -60, y: -15 },
              onClick: ({ cell }) => this.addSuccessEventToStepFunction(cell),
            },
          },
          {
            name: 'button',
            args: {
              markup: [{ tagName: 'g', children: [{ tagName: 'image', selector: 'add-failure', attrs: { ...this.graphService.failurePlusIcon } }] }],
              x: '100%', y: 0, offset: { x: -60, y: 40 },
              onClick: ({ cell }) => this.addFailureEventToStepFunction(cell),
            },
          },
        ]);
      }
      if (this.isAnEventShape(node?.shape) && !this.isAPlaceholderShape(node?.shape)) {
        node.addTools([
          {
            name: 'button',
            args: {
              markup: [{ tagName: 'g', children: [{ tagName: 'image', selector: 'add-function', attrs: { ...this.graphService.plusIcon } }] }],
              x: '100%', y: 0, offset: { x: -28, y: -10 },
              onClick: ({ cell }) => this.addPlaceholderStepFunction(cell.id),
            },
          },
        ]);
      }
    });
  }

  onMouseLeaveNode() {
    this.graph.on('node:mouseleave', ({ node }) => {
      if (node.hasTool('button')) {
        node.removeTool('button');
      }
    });
  }

  setSelectedNode(node: Node) {
    this.displaySettingsForNode(node);
    this.selectedNode = node;
    if (this.lastEnteredNodeId) {
      const oldNode = this.graph.getCellById(this.lastEnteredNodeId);
      oldNode?.removeTools();
    }
    this.lastEnteredNodeId = node.id;
    node.addTools({
      name: 'boundary',
      args: {
        padding: 10,
        attrs: {
          fill: '#7c68fc',
          stroke: '#9254de',
          strokeWidth: 0.5,
          fillOpacity: 0.05
        }
      }
    });
  }

  onCreateWorkflowEvent(workflowEvent: any) {
    let inputDataSample;
    try {
      if (workflowEvent.inputDataSample) {
        inputDataSample = JSON.parse(workflowEvent.inputDataSample);
      }
    } catch (error) {
      this.notificationService.createNotification('warning', 'Warning', 'Invalid JSON format in Input Data Sample.');
      return;
    }
    this.createWorkflowEvent(
      workflowEvent.name,
      inputDataSample,
      workflowEvent?.type ?? this.placeholderEventData.type,
      workflowEvent?.stepFunctionId ?? this.placeholderEventData.stepFunctionId,
      workflowEvent?.id ?? this.placeholderEventData.id
    );
  }

  onStepFunctionSaveButtonClicked() {
    // Placeholder
    console.log('onStepFunctionSaveButtonClicked');
    // save the step function form
    // save custom function

    if (this.selectedWorkflowStepFunction?.mode === 'custom-function-definition') {
      this.updateCustomFunctionUsingTheFormValue();
    }

    this.onStepFunctionFormSubmit(this.selectedWorkflowStepFunction);
  }

  onStepFunctionFormSubmit(stepFunction: any) {
    const params = {
      workflowStepFunction: {
        name: stepFunction.name,
        description: stepFunction.description,
        endpointId: stepFunction.endpointId || null,
        customFunctionDefinitionId: stepFunction?.customFunctionDefinitionId,
        customOrEndpoint: stepFunction?.customOrEndpoint,
        customCode: stepFunction?.customCode,
        projectModel: stepFunction?.projectModel
      },
      publishedEventOnFailureIds: stepFunction.publishedEventsOnFailure || [],
      publishedEventOnSuccessIds: stepFunction.publishedEventsOnSuccess || [],
      subscribedEventIds: stepFunction.subscribedEvents || []
    } as any;

    if (this.stepFunctionMode === StepFunctionModeEnum.Create) {
      this.store.dispatch(WorkflowStepFunctionActions.CreateWorkflowStepFunction({
        payload: {
          workflowStepFunction: params,
          placeholderDataId: this.placeholderStepFunctionData?.id
        }
      }));
    } else {
      this.store.dispatch(WorkflowStepFunctionActions.UpdateWorkflowStepFunction({
        payload: {
          workflowStepFunctionId: this.selectedWorkflowStepFunction.id,
          workflowStepFunctionBody: params
        }
      }));
    }
  }

  private createEventNode(event: WorkflowEventWithRelations) {
    const isSuccess = this.publishedEventsOnSuccess.some(item => item.workflowEventId === event.id);
    const isPublished = [...this.publishedEventsOnSuccess, ...this.publishedEventsOnFailure].some(item => item.workflowEventId === event.id);
    return {
      id: event.id,
      shape: isPublished ? (isSuccess ? 'workflowEventSuccess' : 'workflowEventFailure') : 'workflowEvent',
      label: this.getTrimmedLabel(event.name),
      width: EVENT_WIDTH,
      height: EVENT_HEIGHT,
      ports: this.getNodePorts()
    };
  }

  private createStepFunctionNode(step: WorkflowStepFunctionWithRelations) {
    return {
      id: step.id,
      shape: step.mode ? `${STEP_FUNCTION_SHAPE_NAME_PREFIX}-${step.mode}` : STEP_FUNCTION_SHAPE_NAME_PREFIX,
      label: this.getTrimmedLabel(step.name),
      width: STEP_FUNCTION_WIDTH,
      height: STEP_FUNCTION_HEIGHT,
      attrs: {
        label: {
          text: this.getTrimmedLabel(step.name),
          refY: '100%',
          refY2: 13,
          textAnchor: 'middle',
          textVerticalAnchor: 'top',
        },
      },
      ports: this.getNodePorts()
    };
  }

  private getNodePorts() {
    return {
      groups: {
        in: { position: 'left', attrs: { circle: { r: 4, magnet: true, stroke: '#31d0c6', strokeWidth: 2, fill: '#fff' } } },
        out: { position: 'right', attrs: { circle: { r: 4, magnet: true, stroke: '#31d0c6', strokeWidth: 2, fill: '#fff' } } },
      },
      items: [
        { id: 'in', group: 'in' },
        { id: 'out', group: 'out' }
      ]
    };
  }

  private createSubscribedEdge(relation: WorkflowStepFunctionSubscribedEvent) {
    return {
      id: relation.id,
      source: { cell: relation.workflowEventId, port: 'out' },
      target: { cell: relation.workflowStepFunctionId, port: 'in' },
      shape: 'edge',
    };
  }

  private createPublishedEdge(relation: WorkflowStepFunctionPublishedEventOnSuccess | WorkflowStepFunctionPublishedEventOnFailure, type: 'success' | 'failure') {
    return {
      id: relation.id,
      source: { cell: relation.workflowStepFunctionId, port: 'out' },
      target: { cell: relation.workflowEventId, port: 'in' },
      shape: `${type}-edge`,
    };
  }

  createWorkflowEvent(
    name: string,
    inputDataSample: object | Array<any> | null | undefined,
    eventType?: string,
    stepFunctionId?: string,
    workflowEventId?: string
  ) {
    const eventId = workflowEventId ?? v4();
    this.store.dispatch(WorkflowEventActions.CreateWorkflowEvent({
      payload: {
        workflowEvent: { name, inputDataSample, eventType },
        placeholderData: stepFunctionId ? {
          id: v4(),
          eventId: eventId,
          stepFunctionId: stepFunctionId,
          type: eventType as 'success' | 'failure' | 'subscribe'
        } : undefined
      }
    }));
  }

  addPlaceholderEvent(type: 'success' | 'failure' | 'subscribe', stepFunctionId?: string) {
    this.removePlaceholders();
    const tempEventId = v4();
    const tempEdgeId = v4();
    this.placeholderEventData = {
      id: v4(),
      tempEdgeId,
      tempId: tempEventId,
      stepFunctionId: stepFunctionId,
      type: type
    };
  }

  addEdge(sourceId: string, targetId: string, edgeId?: string, edgeTypeString?: 'failure' | 'success' | 'subscribe') {
    const edgeShape = edgeTypeString === 'failure' ? 'failure-edge' : (edgeTypeString === 'success' ? 'success-edge' : 'edge');
    this.graph.addEdge(
      {
        id: edgeId ?? v4(),
        source: { cell: sourceId, port: 'out' },
        target: { cell: targetId, port: 'in' },
        shape: edgeShape,
      }
    );
  }

  addPlaceholderStepFunction(eventId?: string) {
    this.removePlaceholders();
    this.resetSelectedNode();
    const tempId = v4();
    this.graph.addNode(
      {
        shape: `${STEP_FUNCTION_SHAPE_NAME_PREFIX}${PLACEHOLDER_SHAPE_SUFFIX}`,
        id: tempId,
        width: STEP_FUNCTION_WIDTH,
        height: STEP_FUNCTION_HEIGHT,
        label: 'newStepFunction',
        ports: this.getNodePorts()
      }
    );
    const tempEdgeId = v4();
    if (eventId) {
      this.addEdge(eventId, tempId, tempEdgeId, 'subscribe');
    }
    this.placeholderStepFunctionData = {
      id: v4(),
      tempEdgeId: tempEdgeId,
      tempId: tempId,
      subscribedEventIds: eventId ? [eventId] : []
    };
    this.layout();
  }

  removePlaceholderEvent() {
    if (this.placeholderEventData) {
      this.graph.removeCell(this.placeholderEventData.tempId);
      this.placeholderEventData = undefined;
    }
  }

  removePlaceholderStepFunction() {
    if (this.placeholderStepFunctionData) {
      this.graph.removeCell(this.placeholderStepFunctionData.tempId);
      this.placeholderStepFunctionData = undefined;
    }
  }

  onEditWorkflowEvent(workflowEvent: { workflowEventId: string; workflowEventBody?: WorkflowEventPartial }) {
    let inputDataSample = workflowEvent.workflowEventBody.inputDataSample;
    if (!workflowEvent.workflowEventBody?.description) {
      delete workflowEvent.workflowEventBody.description;
    }
    try {
      if (typeof inputDataSample === 'string' && inputDataSample) {
        inputDataSample = JSON.parse(inputDataSample);
      }
      this.store.dispatch(WorkflowEventActions.UpdateWorkflowEvent({
        payload: {
          workflowEventId: workflowEvent.workflowEventId,
          workflowEventBody: { ...workflowEvent.workflowEventBody, inputDataSample }
        }
      }));
    } catch (error) {
      this.notificationService.createNotification('warning', 'Warning', 'Invalid JSON format.');
    }
  }

  transform(command: string) {
    switch (command) {
      case '%50': this.graph.zoomTo(0.5); break;
      case '%75': this.graph.zoomTo(0.75); break;
      case '%100': this.graph.zoomTo(1); break;
      case '%150': this.graph.zoomTo(1.5); break;
      case '%200': this.graph.zoomTo(2); break;
      case 'zoomIn': this.graph.zoom(0.2); break;
      case 'zoomOut': this.graph.zoom(-0.2); break;
      case 'zoomToFit': this.graph.zoomToFit(); break;
    }
  }

  onZoomLevelChanged(zoomLevel: number) {
    this.graph.zoomTo(zoomLevel / 100);
  }

  onZoomToFit() {
    // this.graph.zoomToFit();
  }

  centerDiagram(): void {
    if (this.graph) {
      this.graph.centerContent();
    }
  }

  onCreateStepFunctionButtonClick() {
    this.islastActiveItemTypeEvent = false;
    this.stepFunctionMode = StepFunctionModeEnum.Create;
    this.initialStepFunctionData = {
      subscribedEventIds: null,
    };
    this.addPlaceholderStepFunction();
  }

  onCreateWorkflowEventButtonClick() {
    this.islastActiveItemTypeEvent = true;
    this.eventMode = EventMode.Create;
    this.addPlaceholderEvent('subscribe');

    this.createWorkflowEvent(
      'NewEvent',
      null,
    );
  }

  onMenuVisibilityChange(visibility: boolean) {
    if (!visibility) {
      this.removePlaceholderStepFunction();
      this.removePlaceholderEvent();
    }
  }

  onEventNameChangeOnCreate(name: string) {
    const placeholderNode = this.graph.getCellById(this.placeholderEventData?.tempId);
    placeholderNode?.prop('label', name);
  }

  onStepFunctionNameChangeOnCreate(name: string) {
    const placeholderNode = this.graph.getCellById(this.placeholderStepFunctionData?.tempId);
    placeholderNode?.prop('label', name);
  }

  onModeChange(selectedMode: string) {
    this.selectedFunctionMode = selectedMode;
  }

  onDataTransformationButtonClick(
    componentMode: WorkflowStepFunctionDataTransformationComponentMode,
    workflowStepFunction: WorkflowStepFunction | WorkflowStepFunctionWithRelations
  ) {
    const workflowStepFunctionWithRelations = this.workflowStepFunctions.find(wfs => wfs.id === workflowStepFunction.id);
    const selectedCustomFunction = this.selectedFunctionMode === WorkflowFunctionTypeOptions.RappiderServices ?
      this.templateWorkflowFunctions?.find(templateWorkflowFunction => templateWorkflowFunction.id === workflowStepFunctionWithRelations.customFunctionDefinitionId)
      : this.customFunctions.find(customFunction => customFunction.id === workflowStepFunctionWithRelations?.customFunctionDefinitionId);
    if (componentMode === WorkflowStepFunctionDataTransformationComponentMode.PreDataTransformation) {
      if (workflowStepFunctionWithRelations?.subscribedEvents?.length && selectedCustomFunction?.requestJSONSample) {
        this.toggleDataTransformationModalVisibility();
        this.store.dispatch(GetPreDataTransformationData({ payload: { workflowStepFunction: workflowStepFunctionWithRelations } }));
      } else {
        if (!workflowStepFunctionWithRelations?.subscribedEvents?.length && !selectedCustomFunction?.requestJSONSample) {
          this.notificationService.createNotification('warning', 'Warning', 'Pre Data transformations cannot be applied to step functions for don\'t have subscribed events and custom functions that do not have a request JSON example.');
        } else if (!selectedCustomFunction?.requestJSONSample) {
          this.notificationService.createNotification('warning', 'Warning', 'Pre Data transformations cannot be applied to step functions for custom functions that do not have a request JSON example.');
        } else if (!workflowStepFunctionWithRelations?.subscribedEvents?.length) {
          this.notificationService.createNotification('warning', 'Warning', 'Pre Data transformations cannot be applied to step functions that don\'t have subscribed events.');
        }
      }
    } else if (componentMode === WorkflowStepFunctionDataTransformationComponentMode.PostDataTransformation) {
      if (workflowStepFunctionWithRelations.publishedEventsOnSuccess?.length && selectedCustomFunction?.responseJSONSample) {
        this.toggleDataTransformationModalVisibility();
        this.store.dispatch(GetPostDataTransformationData({ payload: { workflowStepFunction: workflowStepFunctionWithRelations } }));
      } else {
        if (!workflowStepFunctionWithRelations.publishedEventsOnSuccess?.length && !selectedCustomFunction?.responseJSONSample) {
          this.notificationService.createNotification('warning', 'Warning', 'Post Data transformations cannot be applied to step functions for don\'t have published events and custom functions that do not have a response JSON example.');
        } else if (!selectedCustomFunction?.responseJSONSample) {
          this.notificationService.createNotification('warning', 'Warning', 'Post Data transformations cannot be applied to step functions for custom functions that do not have a response JSON example.');
        } else if (!workflowStepFunctionWithRelations.publishedEventsOnSuccess?.length) {
          this.notificationService.createNotification('warning', 'Warning', 'Post Data transformations cannot be applied to step functions that don\'t have published events.');
        }
      }
    }
  }

  toggleDataTransformationModalVisibility() {
    this.dataTransformationModalVisibility = !this.dataTransformationModalVisibility;
  }

  zoomButtonVisibilityChange() {
    this.zoomButtonVisibility = !this.zoomButtonVisibility;
  }

  addCommentButtonToActiveNode(node: Node) {
    if (this.activeCommentNode && this.activeCommentNode.id !== node.id) {
      this.removeCommentButtonFromNode(this.activeCommentNode);
    }
    this.activeCommentNode = node;
    this.isCommentsNodeVisible = false;

    const factory = (this.injector as any).get(ComponentFactoryResolver).resolveComponentFactory(DiagramNodeToolbarComponent);
    const componentRef = factory.create(this.injector);

    (componentRef.instance as any).onButtonClick = () => {
      this.isCommentsNodeVisible = !this.isCommentsNodeVisible;
      if (this.isCommentsNodeVisible) {
        this.filterCommentsByActiveEntityId();
        this.addCommentComponentNodeToCell(node);
      } else {
        this.removeCommentComponentNodeFromCell(node);
      }
    };
    componentRef.changeDetectorRef.detectChanges();

    node.addTools({
      name: 'button',
      args: {
        html: componentRef.location.nativeElement,
        offset: { x: 20, y: -20 },
        onRemove: () => {
          componentRef.destroy();
        },
      },
    });
  }

  removeCommentButtonFromNode(node: Node) {
    if (node?.hasTool('commentButton')) {
      node.removeTool('button');
    }
  }

  addCommentComponentNodeToCell(cell: Cell): void {
    if (this.activeCommentCell && this.activeCommentCell.id !== cell.id) {
      this.removeCommentComponentNodeFromCell(this.activeCommentCell);
    }

    const position = (cell as Node).getPosition();

    register({
      shape: 'comments',
      width: 400,
      height: 20,
      content: this.commentsTemplate,
      injector: this.injector,
    });


    const commentNode = this.graph.addNode({
      shape: 'comments',
      x: position.x + (this.isAStepFunctionShape(cell.shape) ? 195 : 88),
      y: position.y + (this.isAStepFunctionShape(cell.shape) ? -10 : -8),
      data: {
        ngArguments: {
          activePerson: this.activePerson,
          peopleData: this.activeProject?.people,
          comments: this.displayedComments,
          isLoading: this.isCommentsLoading
        },
      },
    });
    cell.addChild(commentNode);
    this.activeCommentCell = cell;
  }

  removeCommentComponentNodeFromCell(cell: Cell): void {
    const children = cell?.getChildren();
    if (children) {
      const commentNode = children.find(child => child.shape === 'comments');
      if (commentNode) {
        cell.removeChild(commentNode);
      }
    }
    if (this.activeCommentCell?.id === cell.id) {
      this.activeCommentCell = null;
    }
  }

  resetGraphCommentComponentsState() {
    if (this.activeCommentCell) {
      this.removeCommentComponentNodeFromCell(this.activeCommentCell);
    }
    if (this.activeCommentNode) {
      this.removeCommentButtonFromNode(this.activeCommentNode);
    }
    this.activeCommentCell = null;
    this.activeCommentNode = null;
  }

  setActiveCommentCellData() {
    if (this.activeCommentCell?.hasChanged('children')) {
      const commentNode = this.activeCommentCell.getChildren()?.find(c => c.shape === 'comments');
      if (commentNode) {
        commentNode.setData({
          ngArguments: { ...commentNode.getData().ngArguments, comments: this.displayedComments }
        });
      }
    }
  }

  setActiveCommentCellLoading() {
    if (this.activeCommentCell?.hasChanged('children')) {
      const commentNode = this.activeCommentCell.getChildren()?.find(c => c.shape === 'comments');
      if (commentNode) {
        commentNode.setData({
          ngArguments: { ...commentNode.getData().ngArguments, isLoading: this.isCommentsLoading }
        });
      }
    }
  }

  onCreateComment(comment: any) {
    if (this.activeCommentCell?.id && comment) {
      const nodeShape = this.activeCommentCell.shape;
      const newComment: NewDiagramComment = {
        category: this.isAStepFunctionShape(nodeShape) ? CommentCategory.BEWorkflowStepFunction : CommentCategory.BEWorkflowEvent,
        content: comment.content,
        assignedToPersonId: comment.assignedToPersonId,
        relatedEntityId: this.activeCommentCell.id
      };
      this.store.dispatch(CreateDiagramComment({ comment: newComment }));
    }
  }

  onEditComment(comment: any) {
    const { id, ...body } = comment;
    if (id) {
      this.store.dispatch(UpdateDiagramComment({ commentId: id, comment: body }));
    }
  }

  onDeleteComment(comment: any) {
    if (comment.id && comment.details?.id) {
      this.store.dispatch(DeleteDiagramComment({ commentId: comment.id, diagrmaCommentId: comment.details.id }));
    }
  }

  filterCommentsByActiveEntityId() {
    if (this.lastEnteredNodeId) {
      this.displayedComments = this.comments?.filter(c => c.details?.relatedEntityId === this.lastEnteredNodeId);
    }
  }

  functionFromLibrarySelected(event: any) {
    // Placeholder
  }

  functionTypeSelectorMenuItemClicked(event: { key: WorkflowStepFunctionTypeOption }) {
    switch (event.key) {
      case WorkflowStepFunctionTypeOption.Integrate:
        this.showIntegrationFunctions = true;
        break;
      case WorkflowStepFunctionTypeOption.AIGeneratedCustomFunction:
        this.displayAIGeneratedCustomFunctionForm();
        break;
      case WorkflowStepFunctionTypeOption.ProjectEndpoints:
        this.displayProjectEndpointsForm();
        break;
      case WorkflowStepFunctionTypeOption.AI:
      case WorkflowStepFunctionTypeOption.DataTransformation:
      case WorkflowStepFunctionTypeOption.RouteWithConditions:
      case WorkflowStepFunctionTypeOption.BlankFunction:
        this.generateEmptyWorkflowStepFunction(event.key);
        break;
    }
  }

  generateEmptyWorkflowStepFunction(selectedWorkflowStepFunctionTypeOption: WorkflowStepFunctionTypeOption) {
    this.workflowService.generateEmptyStepFunction(
      this.activeProject?.id,
      selectedWorkflowStepFunctionTypeOption,
      this.placeholderStepFunctionData?.id,
      this.placeholderStepFunctionData?.subscribedEventIds,
      this.workflowStepFunctions,
      this.aiFunctionCalls
    );
  }

  displayAIGeneratedCustomFunctionForm() {
    this.showFunctionTypeSelector = false;
    this.showAIGeneratedCustomFunctionForm = true;
    this.viewMode = SettingViewMode.AIGeneratedCustomFunction;
  }

  displayProjectEndpointsForm() {
    this.showFunctionTypeSelector = false;
    this.showProjectEndpointsForm = true;
    this.viewMode = SettingViewMode.ProjectEndpoints;
  }

  displayStepFunctions() {
    this.showIntegrationFunctions = false;
    this.showAIGeneratedCustomFunctionForm = false;
    this.showProjectEndpointsForm = false;
    this.showFunctionTypeSelector = true;
    this.viewMode = SettingViewMode.FunctionTypeSelector;
  }

  onCreateCustomFunctionWithAIFormSubmit(event) {
    console.log('onCreateCustomFunctionWithAIFormSubmit', event);
  }

  onSelectProjectEndpointFormSubmit(event: any) {
    console.log('onSelectProjectEndpointFormSubmit', event);
  }

  onDeleteWorkflow(id) {
    this.store.dispatch(DeleteWorkflowByEventId({ payload: { id } }));
  }

  onDeleteEvent(workflowEventId) {
    this.store.dispatch(DeleteWorkflowEvent({ payload: { workflowEventId } }));
  }

  onStepFunctionDeleteButtonClicked() {
    this.store.dispatch(DeleteWorkflowStepFunction({ payload: { workflowStepFunctionId: this.selectedWorkflowStepFunction?.id } }));
  }

  // #endregion
}
